import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Building2, 
  MapPin, 
  Search, 
  Copy, 
  Check, 
  Shield, 
  PhoneCall, 
  RotateCcw,
  LayoutGrid,
  Table as TableIcon,
  HeartPulse,
  Flame,
  ChevronLeft,
  ChevronRight,
  Lock
} from 'lucide-react';
import SEO from '../components/SEO';
import api from '../utils/api';
import { showSuccess, showInfo } from '../utils/toast';
import { isUserLoggedIn, isAdminLoggedIn } from '../utils/auth';
import { trackPhoneView } from '../utils/phoneLogTracker';

const DEPARTMENT_THEMES = {
  'Administration': {
    badge: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800',
    dot: 'bg-blue-600',
    iconColor: 'text-blue-600'
  },
  'Police & Law': {
    badge: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800',
    dot: 'bg-red-600',
    iconColor: 'text-red-600'
  },
  'Revenue & Tehsil': {
    badge: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
    dot: 'bg-amber-600',
    iconColor: 'text-amber-600'
  },
  'Health & Medical': {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
    dot: 'bg-emerald-600',
    iconColor: 'text-emerald-600'
  },
  'Rural & Panchayat': {
    badge: 'bg-lime-50 text-lime-800 border-lime-200 dark:bg-lime-950/40 dark:text-lime-300 dark:border-lime-800',
    dot: 'bg-lime-600',
    iconColor: 'text-lime-600'
  },
  'Municipal Services': {
    badge: 'bg-cyan-50 text-cyan-800 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300 dark:border-cyan-800',
    dot: 'bg-cyan-600',
    iconColor: 'text-cyan-600'
  },
  'Electricity & Power': {
    badge: 'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-800',
    dot: 'bg-yellow-600',
    iconColor: 'text-yellow-600'
  },
  'Public Works & Water': {
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800',
    dot: 'bg-indigo-600',
    iconColor: 'text-indigo-600'
  },
  'Education & Colleges': {
    badge: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800',
    dot: 'bg-purple-600',
    iconColor: 'text-purple-600'
  },
  'Agriculture & Food': {
    badge: 'bg-teal-50 text-teal-800 border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800',
    dot: 'bg-teal-600',
    iconColor: 'text-teal-600'
  },
  'Social Welfare & Others': {
    badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800',
    dot: 'bg-rose-600',
    iconColor: 'text-rose-600'
  }
};

const DEFAULT_THEME = {
  badge: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
  dot: 'bg-gray-500',
  iconColor: 'text-gray-600'
};

const QUICK_EMERGENCY_OFFICERS = [
  {
    role: 'Deputy Commissioner',
    name: 'DC Office, Fatehabad',
    phone: '01662-230001',
    sub: 'Office: 230001/02',
    icon: Building2,
    badgeColor: 'bg-blue-600 text-white'
  },
  {
    role: 'Superintendent of Police (SP)',
    name: 'S.P. Fatehabad',
    phone: '8814011700',
    sub: 'Office: 230005',
    icon: Shield,
    badgeColor: 'bg-red-600 text-white'
  },
  {
    role: 'Civil Surgeon (CMO)',
    name: 'Health Department Fatehabad',
    phone: '9416325324',
    sub: 'Office: 230201',
    icon: HeartPulse,
    badgeColor: 'bg-emerald-600 text-white'
  },
  {
    role: 'Fire Safety Officer',
    name: 'Fire Station Fatehabad',
    phone: '9468333251',
    sub: 'Office: 220100',
    icon: Flame,
    badgeColor: 'bg-orange-600 text-white'
  }
];

const Officers = () => {
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [areas, setAreas] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [copiedPhone, setCopiedPhone] = useState(null);

  const clientLoggedIn = isUserLoggedIn() || isAdminLoggedIn();
  const [isLoggedInServer, setIsLoggedInServer] = useState(false);
  const isLoggedIn = clientLoggedIn || isLoggedInServer;

  // Pagination
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, limit: 30 });

  // Fetch metadata once
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const res = await api.get('/officers/meta');
        if (res.data) {
          setDepartments(res.data.departments || []);
          setAreas(res.data.areas || []);
          setTotalCount(res.data.totalCount || 0);
        }
      } catch (err) {
        console.error('Failed to load officers meta', err);
      }
    };
    fetchMeta();
  }, []);

  const fetchOfficers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (selectedDept) params.append('department', selectedDept);
      if (selectedArea) params.append('area', selectedArea);
      params.append('page', page);
      params.append('limit', 30);

      const res = await api.get(`/officers?${params.toString()}`);
      if (res.data) {
        setOfficers(res.data.officers || []);
        if (res.data.isLoggedIn !== undefined) {
          setIsLoggedInServer(res.data.isLoggedIn);
        }
        if (res.data.pagination) {
          setPagination(res.data.pagination);
        }
      }
    } catch (err) {
      console.error('Failed to fetch officers:', err);
    } finally {
      setLoading(false);
    }
  }, [search, selectedDept, selectedArea, page]);

  // Fetch officers with debounce on search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOfficers();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchOfficers]);

  const handleCopy = (phone, e, officer = null) => {
    if (e) e.stopPropagation();
    if (!isLoggedIn) {
      showInfo('Please login to view and copy contact numbers', 'Authentication Required');
      return;
    }
    if (!phone) return;
    navigator.clipboard.writeText(phone);
    setCopiedPhone(phone);
    showSuccess(`Copied ${phone} to clipboard`);
    trackPhoneView({
      targetType: 'officer',
      targetId: officer?._id,
      targetName: officer?.name || officer?.designation || 'Officer Contact',
      targetCategory: officer?.department || 'Administration',
      viewedPhone: phone,
      action: 'copy_number'
    });
    setTimeout(() => setCopiedPhone(null), 2000);
  };

  const handleResetFilters = () => {
    setSearch('');
    setSelectedDept('');
    setSelectedArea('');
    setPage(1);
  };



  // Structured Data for SEO
  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'GovernmentOrganization',
    name: 'District Administration Fatehabad - Official Directory',
    description: 'Official directory of administrative and government officers of District Fatehabad, Haryana.',
    areaServed: 'Fatehabad, Haryana, India',
    telephone: '+91-1662-230001'
  }), []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <SEO
        title="District Fatehabad Officers Directory | Helpline & Government Contacts"
        description="Official Telephone Directory of Government Officers in District Fatehabad (Haryana). Find contact numbers, office and residence phones for DC, SP, SDMs, Tehsildars, and all district departments."
        canonical="/officers"
        jsonLd={jsonLd}
      />

      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 text-white p-6 sm:p-10 shadow-2xl border border-indigo-700/30">
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 w-80 h-80 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />

          <div className="relative z-10 max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 backdrop-blur-md border border-white/20 text-blue-200">
              <Building2 className="w-3.5 h-3.5" />
              <span>Government Directory • District Fatehabad</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight text-white">
              Telephone Directory of District Officers
            </h1>
            <p className="text-base sm:text-lg text-indigo-100/90 leading-relaxed font-normal">
              Official phone & contact numbers of key administrative, police, civil services, medical, and revenue officers across Fatehabad, Ratia, Tohana, and Hisar divisions.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2 text-xs sm:text-sm text-indigo-200">
              <span className="flex items-center gap-1.5 font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                {totalCount || 180} Verified Officers Listed
              </span>
              <span>•</span>
              <span>Updated Official Gazette Records</span>
              <span>•</span>

            </div>
          </div>
        </div>

        {/* Quick Emergency / Priority Helplines */}
        <div>
          <div className="flex items-center justify-between mb-3 px-1">
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <PhoneCall className="w-4 h-4 text-blue-600" />
              Key District Helplines & Emergency Contacts
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {QUICK_EMERGENCY_OFFICERS.map((item, idx) => (
              <div
                key={idx}
                className="bg-white dark:bg-gray-900 border border-gray-200/80 dark:border-gray-800 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 block mb-1">
                      {item.role}
                    </span>
                    <h3 className="font-bold text-gray-900 dark:text-white text-base leading-tight">
                      {item.name}
                    </h3>
                  </div>
                  <div className={`p-2.5 rounded-xl ${item.badgeColor}`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {item.sub}
                  </div>
                  {isLoggedIn ? (
                    <a
                      href={`tel:${item.phone.replace(/[^0-9+]/g, '')}`}
                      onClick={() => trackPhoneView({
                        targetType: 'officer',
                        targetName: item.name,
                        targetCategory: item.role,
                        viewedPhone: item.phone,
                        action: 'call_click'
                      })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 rounded-xl text-xs font-bold transition-all"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      Call Now
                    </a>
                  ) : (
                    <Link
                      to="/login"
                      state={{ from: '/officers' }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 hover:bg-amber-600 hover:text-white rounded-xl text-xs font-bold transition-all border border-amber-200 dark:border-amber-800"
                    >
                      <Lock className="w-3.5 h-3.5" />
                      Login to Call
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Not Logged In Notice Banner */}
        {!isLoggedIn && (
          <div className="flex items-center gap-3.5 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-2xl shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center text-amber-600 dark:text-amber-400 shrink-0">
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
                Direct Calling & Contact Numbers are Protected
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                <Link to="/login" state={{ from: '/officers' }} className="underline font-bold hover:text-amber-950 dark:hover:text-amber-100">Login</Link> or <Link to="/signup" state={{ from: '/officers' }} className="underline font-bold hover:text-amber-950 dark:hover:text-amber-100">Sign up</Link> to view unmasked telephone numbers and make direct phone calls to officers.
              </p>
            </div>
            <Link
              to="/login"
              state={{ from: '/officers' }}
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shrink-0 transition-colors shadow-sm"
            >
              <Lock className="w-3.5 h-3.5" />
              Login to Call
            </Link>
          </div>
        )}

        {/* Search & Filter Toolbar */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 sm:p-6 shadow-sm space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4 items-center">
            
            {/* Search Input */}
            <div className="md:col-span-5 relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, designation, department, or phone..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-gray-900 dark:text-white placeholder-gray-400"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Department Dropdown */}
            <div className="md:col-span-4">
              <select
                value={selectedDept}
                onChange={(e) => {
                  setSelectedDept(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-gray-900 dark:text-white"
              >
                <option value="">All Departments ({departments.length})</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>

            {/* Area Filter */}
            <div className="md:col-span-3">
              <select
                value={selectedArea}
                onChange={(e) => {
                  setSelectedArea(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-gray-900 dark:text-white"
              >
                <option value="">All Areas ({areas.length})</option>
                {areas.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Department Pills for Quick One-Click Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 pt-1 no-scrollbar text-xs">
            <button
              onClick={() => { setSelectedDept(''); setPage(1); }}
              className={`px-3 py-1.5 rounded-full font-medium whitespace-nowrap transition-all ${
                selectedDept === ''
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
              }`}
            >
              All Departments
            </button>
            {departments.map((dept) => (
              <button
                key={dept}
                onClick={() => { setSelectedDept(dept); setPage(1); }}
                className={`px-3 py-1.5 rounded-full font-medium whitespace-nowrap transition-all ${
                  selectedDept === dept
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                {dept}
              </button>
            ))}
          </div>

          {/* View Mode & Active Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                Found {pagination.total || officers.length} Officers
              </span>
              {(search || selectedDept || selectedArea) && (
                <button
                  onClick={handleResetFilters}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium ml-2"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset Filters
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md ${
                    viewMode === 'grid'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                  }`}
                  title="Grid View"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-md ${
                    viewMode === 'table'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                  }`}
                  title="Table View"
                >
                  <TableIcon className="w-4 h-4" />
                </button>
              </div>


            </div>
          </div>
        </div>

        {/* Directory Content */}
        {loading ? (
          <div className="py-20 text-center space-y-3">
            <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Loading officers directory...</p>
          </div>
        ) : officers.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-12 text-center space-y-3">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto text-gray-400">
              <Search className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-lg text-gray-900 dark:text-white">No officers found</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              We couldn't find any officer matching your search criteria. Try clearing some filters or searching with a different term.
            </p>
            <button
              onClick={handleResetFilters}
              className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
            >
              Reset All Filters
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          
          /* GRID VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {officers.map((officer) => {
              const theme = DEPARTMENT_THEMES[officer.department] || DEFAULT_THEME;

              return (
                <div
                  key={officer._id}
                  className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/90 dark:border-gray-800 p-5 shadow-sm hover:shadow-md hover:border-blue-300 dark:hover:border-blue-700 transition-all flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    {/* Top Row: S.N & Department */}
                    <div className="flex items-start justify-between gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${theme.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${theme.dot}`} />
                        {officer.department}
                      </span>

                      {officer.serialNo && (
                        <span className="text-xs font-mono font-bold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-md">
                          #{officer.serialNo}
                        </span>
                      )}
                    </div>

                    {/* Officer Name & Designation */}
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white leading-snug">
                        {officer.name}
                      </h3>
                      <p className="text-sm font-semibold text-blue-700 dark:text-blue-400 mt-0.5">
                        {officer.designation}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <MapPin className="w-3.5 h-3.5 text-gray-400" />
                        <span>{officer.area || 'Fatehabad'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Numbers Section */}
                  <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-2 text-xs">
                    
                    {/* Mobile Contact No. */}
                    {officer.contactNo && (
                      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 rounded-xl px-3 py-2 border border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2 overflow-hidden">
                          <Phone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span className="text-gray-500 dark:text-gray-400 font-medium">Contact:</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {officer.contactNo}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          <button
                            onClick={(e) => handleCopy(officer.contactNo, e, officer)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            title={isLoggedIn ? "Copy contact number" : "Login to view & copy"}
                          >
                            {copiedPhone === officer.contactNo ? (
                              <Check className="w-3.5 h-3.5 text-green-600" />
                            ) : !isLoggedIn ? (
                              <Lock className="w-3.5 h-3.5 text-amber-500" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                          {isLoggedIn ? (
                            <a
                              href={`tel:${officer.contactNo.split(/[,/]/)[0].trim().replace(/[^0-9+]/g, '')}`}
                              onClick={() => trackPhoneView({
                                targetType: 'officer',
                                targetId: officer._id,
                                targetName: officer.name,
                                targetCategory: officer.department,
                                viewedPhone: officer.contactNo,
                                action: 'call_click'
                              })}
                              className="px-2.5 py-1 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 transition-colors"
                            >
                              Call
                            </a>
                          ) : (
                            <Link
                              to="/login"
                              state={{ from: '/officers' }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-lg font-bold hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors"
                            >
                              <Lock className="w-3 h-3" />
                              Login to Call
                            </Link>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Office Phone */}
                    {officer.officePhone && (
                      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 rounded-xl px-3 py-2 border border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2 overflow-hidden">
                          <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                          <span className="text-gray-500 dark:text-gray-400 font-medium">Office:</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {officer.officePhone}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          <button
                            onClick={(e) => handleCopy(officer.officePhone, e, officer)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            title={isLoggedIn ? "Copy office phone" : "Login to view & copy"}
                          >
                            {copiedPhone === officer.officePhone ? (
                              <Check className="w-3.5 h-3.5 text-green-600" />
                            ) : !isLoggedIn ? (
                              <Lock className="w-3.5 h-3.5 text-amber-500" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                          {isLoggedIn ? (
                            <a
                              href={`tel:${officer.officePhone.split(/[,/]/)[0].trim().replace(/[^0-9+]/g, '')}`}
                              onClick={() => trackPhoneView({
                                targetType: 'officer',
                                targetId: officer._id,
                                targetName: officer.name,
                                targetCategory: officer.department,
                                viewedPhone: officer.officePhone,
                                action: 'call_click'
                              })}
                              className="px-2.5 py-1 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors"
                            >
                              Call
                            </a>
                          ) : (
                            <Link
                              to="/login"
                              state={{ from: '/officers' }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-lg font-bold hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors"
                            >
                              <Lock className="w-3 h-3" />
                              Login to Call
                            </Link>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Residence Phone */}
                    {officer.resiPhone && (
                      <div className="flex items-center justify-between text-gray-500 dark:text-gray-400 px-1 pt-1">
                        <span>Resi: {officer.resiPhone}</span>
                        {isLoggedIn ? (
                          <a
                            href={`tel:${officer.resiPhone.split(/[,/]/)[0].trim().replace(/[^0-9+]/g, '')}`}
                            onClick={() => trackPhoneView({
                              targetType: 'officer',
                              targetId: officer._id,
                              targetName: officer.name,
                              targetCategory: officer.department,
                              viewedPhone: officer.resiPhone,
                              action: 'call_click'
                            })}
                            className="hover:text-blue-600 font-medium"
                          >
                            Call Resi
                          </a>
                        ) : (
                          <Link
                            to="/login"
                            state={{ from: '/officers' }}
                            className="hover:text-amber-600 font-medium inline-flex items-center gap-1"
                          >
                            <Lock className="w-3 h-3" /> Login
                          </Link>
                        )}
                      </div>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          
          /* COMPACT TABLE VIEW */
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold uppercase tracking-wider text-[11px]">
                  <tr>
                    <th className="px-4 py-3.5">S.N</th>
                    <th className="px-4 py-3.5">Officer Name</th>
                    <th className="px-4 py-3.5">Designation</th>
                    <th className="px-4 py-3.5">Department</th>
                    <th className="px-4 py-3.5">Area</th>
                    <th className="px-4 py-3.5">Contact No.</th>
                    <th className="px-4 py-3.5">Office Phone</th>
                    <th className="px-4 py-3.5">Resi Phone</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {officers.map((officer) => (
                    <tr 
                      key={officer._id} 
                      className="hover:bg-blue-50/40 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-gray-500 font-bold">{officer.serialNo}</td>
                      <td className="px-4 py-3 font-bold text-gray-900 dark:text-white whitespace-nowrap">{officer.name}</td>
                      <td className="px-4 py-3 font-medium text-blue-700 dark:text-blue-400">{officer.designation}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                          {officer.department}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400">{officer.area || 'Fatehabad'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {officer.contactNo ? (
                          isLoggedIn ? (
                            <div className="flex items-center gap-1.5 font-semibold text-emerald-700 dark:text-emerald-400">
                              <a
                                href={`tel:${officer.contactNo.split(/[,/]/)[0].trim().replace(/[^0-9+]/g, '')}`}
                                onClick={() => trackPhoneView({
                                  targetType: 'officer',
                                  targetId: officer._id,
                                  targetName: officer.name,
                                  targetCategory: officer.department,
                                  viewedPhone: officer.contactNo,
                                  action: 'call_click'
                                })}
                                className="hover:underline"
                              >
                                {officer.contactNo}
                              </a>
                              <button onClick={(e) => handleCopy(officer.contactNo, e, officer)} className="text-gray-400 hover:text-gray-600">
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <Link
                              to="/login"
                              state={{ from: '/officers' }}
                              className="inline-flex items-center gap-1 font-semibold text-amber-600 dark:text-amber-400 hover:underline"
                            >
                              <Lock className="w-3 h-3" />
                              <span>{officer.contactNo}</span>
                            </Link>
                          )
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {officer.officePhone ? (
                          isLoggedIn ? (
                            <a
                              href={`tel:${officer.officePhone.split(/[,/]/)[0].trim().replace(/[^0-9+]/g, '')}`}
                              onClick={() => trackPhoneView({
                                targetType: 'officer',
                                targetId: officer._id,
                                targetName: officer.name,
                                targetCategory: officer.department,
                                viewedPhone: officer.officePhone,
                                action: 'call_click'
                              })}
                              className="font-semibold text-blue-700 dark:text-blue-400 hover:underline"
                            >
                              {officer.officePhone}
                            </a>
                          ) : (
                            <Link
                              to="/login"
                              state={{ from: '/officers' }}
                              className="inline-flex items-center gap-1 font-semibold text-amber-600 dark:text-amber-400 hover:underline"
                            >
                              <Lock className="w-3 h-3" />
                              <span>{officer.officePhone}</span>
                            </Link>
                          )
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {officer.resiPhone ? (
                          isLoggedIn ? (
                            officer.resiPhone
                          ) : (
                            <span className="inline-flex items-center gap-1 text-gray-400">
                              <Lock className="w-3 h-3 text-amber-500" />
                              {officer.resiPhone}
                            </span>
                          )
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between bg-white dark:bg-gray-900 px-4 py-3 sm:px-6 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Showing page <span className="font-semibold text-gray-900 dark:text-white">{page}</span> of{' '}
              <span className="font-semibold text-gray-900 dark:text-white">{pagination.pages}</span> (
              {pagination.total} Total)
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => {
                  setPage((prev) => Math.max(1, prev - 1));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <button
                disabled={page >= pagination.pages}
                onClick={() => {
                  setPage((prev) => Math.min(pagination.pages, prev + 1));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Disclaimer & Info Box */}
        <div className="bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-900/50 rounded-2xl p-5 text-xs text-blue-900 dark:text-blue-300 leading-relaxed">
          <p className="font-bold text-sm mb-1 flex items-center gap-1.5">
            <Shield className="w-4 h-4" />
            Official District Gazette Information Notice
          </p>
          <p>
            The above telephone numbers are compiled from the official directory of District Administration Fatehabad (Haryana). Phone numbers and office designations may change upon transfer of government officers. For urgent police emergencies dial 112, for medical emergencies dial 108, and for fire emergencies dial 101.
          </p>
        </div>

      </div>
    </div>
  );
};

export default Officers;
